import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../components/AuthContext';
import {
  getPasskeys,
  verwijderPasskey,
  passkeyRegistratieStart,
  passkeyRegistratieVoltooi,
  opslaanProfiel,
  wijzigWachtwoord,
} from '../api/api';
import {
  bereidRegistratieVoor,
  registratieNaarJson,
  passkeysBeschikbaar,
} from '../util/webauthn';

function formatteerDatum(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

function Melding({ melding }) {
  if (!melding) return null;
  return (
    <div className={`alert ${melding.type === 'ok' ? 'alert-success' : 'alert-error'}`} style={{ margin: '12px 0' }}>
      {melding.tekst}
    </div>
  );
}

export default function Account() {
  const { gebruiker, verversGebruiker } = useAuth();

  // --- Profiel ---
  const [voornaam, setVoornaam] = useState('');
  const [tussenvoegsel, setTussenvoegsel] = useState('');
  const [achternaam, setAchternaam] = useState('');
  const [profielBezig, setProfielBezig] = useState(false);
  const [profielMelding, setProfielMelding] = useState(null);

  useEffect(() => {
    if (gebruiker) {
      setVoornaam(gebruiker.voornaam || '');
      setTussenvoegsel(gebruiker.tussenvoegsel || '');
      setAchternaam(gebruiker.achternaam || '');
    }
  }, [gebruiker]);

  const slaProfielOp = async () => {
    setProfielMelding(null);
    if (!voornaam.trim() || !achternaam.trim()) {
      setProfielMelding({ type: 'error', tekst: 'Voornaam en achternaam zijn verplicht.' });
      return;
    }
    setProfielBezig(true);
    try {
      await opslaanProfiel(voornaam.trim(), tussenvoegsel.trim(), achternaam.trim());
      await verversGebruiker();
      setProfielMelding({ type: 'ok', tekst: 'Profiel opgeslagen.' });
    } catch {
      setProfielMelding({ type: 'error', tekst: 'Opslaan mislukt. Probeer het opnieuw.' });
    } finally {
      setProfielBezig(false);
    }
  };

  // --- Wachtwoord ---
  const [huidigWw, setHuidigWw] = useState('');
  const [nieuwWw, setNieuwWw] = useState('');
  const [nieuwWw2, setNieuwWw2] = useState('');
  const [wwBezig, setWwBezig] = useState(false);
  const [wwMelding, setWwMelding] = useState(null);

  const wijzigWw = async () => {
    setWwMelding(null);
    if (nieuwWw.length < 10) {
      setWwMelding({ type: 'error', tekst: 'Nieuw wachtwoord moet minstens 10 tekens zijn.' });
      return;
    }
    if (nieuwWw !== nieuwWw2) {
      setWwMelding({ type: 'error', tekst: 'De nieuwe wachtwoorden komen niet overeen.' });
      return;
    }
    setWwBezig(true);
    try {
      await wijzigWachtwoord(huidigWw, nieuwWw);
      setHuidigWw(''); setNieuwWw(''); setNieuwWw2('');
      setWwMelding({ type: 'ok', tekst: 'Wachtwoord gewijzigd.' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setWwMelding({ type: 'error', tekst: detail || 'Wijzigen mislukt. Probeer het opnieuw.' });
    } finally {
      setWwBezig(false);
    }
  };

  // --- Passkeys ---
  const [passkeys, setPasskeys] = useState([]);
  const [pkLaden, setPkLaden] = useState(true);
  const [pkBezig, setPkBezig] = useState(false);
  const [apparaatnaam, setApparaatnaam] = useState('');
  const [pkMelding, setPkMelding] = useState(null);

  const laadPasskeys = useCallback(async () => {
    try {
      const res = await getPasskeys();
      setPasskeys(res.data);
    } catch {
      setPkMelding({ type: 'error', tekst: 'Passkeys konden niet worden geladen.' });
    } finally {
      setPkLaden(false);
    }
  }, []);

  useEffect(() => { laadPasskeys(); }, [laadPasskeys]);

  const registreer = async () => {
    setPkMelding(null);
    setPkBezig(true);
    try {
      const opties = (await passkeyRegistratieStart()).data;
      const credential = await navigator.credentials.create(bereidRegistratieVoor(opties));
      const naam = apparaatnaam.trim() || 'Onbenoemd apparaat';
      await passkeyRegistratieVoltooi(registratieNaarJson(credential), naam);
      setPkMelding({ type: 'ok', tekst: 'Passkey geregistreerd.' });
      setApparaatnaam('');
      await laadPasskeys();
    } catch (err) {
      if (err?.name === 'NotAllowedError') {
        setPkMelding({ type: 'error', tekst: 'Registratie afgebroken of niet toegestaan. Probeer het opnieuw.' });
      } else if (err?.name === 'InvalidStateError') {
        setPkMelding({ type: 'error', tekst: 'Dit apparaat heeft al een passkey voor dit account.' });
      } else {
        setPkMelding({ type: 'error', tekst: 'Registreren mislukt. Probeer het opnieuw.' });
      }
    } finally {
      setPkBezig(false);
    }
  };

  const verwijder = async (id, naam) => {
    if (!window.confirm(`Passkey "${naam || 'onbenoemd'}" verwijderen?`)) return;
    setPkMelding(null);
    try {
      await verwijderPasskey(id);
      await laadPasskeys();
    } catch {
      setPkMelding({ type: 'error', tekst: 'Verwijderen mislukt.' });
    }
  };

  const labelStijl = { marginBottom: 6, marginTop: 12 };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Account &amp; beveiliging</h1>
          <p className="page-subtitle">{gebruiker?.naam}</p>
        </div>
      </div>

      {/* Profiel */}
      <div className="card" style={{ maxWidth: 640, marginBottom: 20 }}>
        <div className="card-header"><h2 className="card-title">Profiel</h2></div>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 0 }}>
          Je naam verschijnt bij opmerkingen en mutaties die je vastlegt.
        </p>
        <Melding melding={profielMelding} />
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 360 }}>
          <label className="field-label" style={{ marginBottom: 6 }}>Voornaam</label>
          <input className="input" value={voornaam} onChange={(e) => setVoornaam(e.target.value)} />
          <label className="field-label" style={labelStijl}>Tussenvoegsel <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optioneel)</span></label>
          <input className="input" value={tussenvoegsel} onChange={(e) => setTussenvoegsel(e.target.value)} placeholder="bijv. den, van der" />
          <label className="field-label" style={labelStijl}>Achternaam</label>
          <input className="input" value={achternaam} onChange={(e) => setAchternaam(e.target.value)} />
          <button className="btn btn-primary" onClick={slaProfielOp} disabled={profielBezig} style={{ marginTop: 16, alignSelf: 'flex-start' }}>
            {profielBezig ? <><span className="spinner" /> Opslaan...</> : 'Profiel opslaan'}
          </button>
        </div>
      </div>

      {/* Wachtwoord */}
      <div className="card" style={{ maxWidth: 640, marginBottom: 20 }}>
        <div className="card-header"><h2 className="card-title">Wachtwoord wijzigen</h2></div>
        <Melding melding={wwMelding} />
        <div style={{ display: 'flex', flexDirection: 'column', maxWidth: 360 }}>
          <label className="field-label" style={{ marginBottom: 6 }}>Huidig wachtwoord</label>
          <input className="input" type="password" autoComplete="current-password" value={huidigWw} onChange={(e) => setHuidigWw(e.target.value)} />
          <label className="field-label" style={labelStijl}>Nieuw wachtwoord <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(min. 10 tekens)</span></label>
          <input className="input" type="password" autoComplete="new-password" value={nieuwWw} onChange={(e) => setNieuwWw(e.target.value)} />
          <label className="field-label" style={labelStijl}>Nieuw wachtwoord (nogmaals)</label>
          <input className="input" type="password" autoComplete="new-password" value={nieuwWw2} onChange={(e) => setNieuwWw2(e.target.value)} />
          <button className="btn btn-primary" onClick={wijzigWw} disabled={wwBezig || !huidigWw || !nieuwWw} style={{ marginTop: 16, alignSelf: 'flex-start' }}>
            {wwBezig ? <><span className="spinner" /> Wijzigen...</> : 'Wachtwoord wijzigen'}
          </button>
        </div>
      </div>

      {/* Passkeys */}
      <div className="card" style={{ maxWidth: 640 }}>
        <div className="card-header"><h2 className="card-title">Passkeys</h2></div>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 0 }}>
          Met een passkey log je in met Touch&nbsp;ID of Face&nbsp;ID, zonder wachtwoord.
          Je wachtwoord blijft werken als terugval.
        </p>
        <Melding melding={pkMelding} />
        {pkLaden ? (
          <div style={{ padding: 20, textAlign: 'center' }}><span className="spinner" /></div>
        ) : passkeys.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Nog geen passkeys geregistreerd.</p>
        ) : (
          <div className="table-wrap" style={{ marginTop: 8 }}>
            <table className="table">
              <thead>
                <tr><th>Apparaat</th><th>Geregistreerd</th><th>Laatst gebruikt</th><th></th></tr>
              </thead>
              <tbody>
                {passkeys.map((p) => (
                  <tr key={p.passkey_id}>
                    <td>{p.apparaatnaam || 'Onbenoemd apparaat'}</td>
                    <td>{formatteerDatum(p.aangemaakt_op)}</td>
                    <td>{formatteerDatum(p.laatst_gebruikt_op)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn btn-ghost" style={{ fontSize: 13, padding: '4px 10px', color: 'var(--danger)' }} onClick={() => verwijder(p.passkey_id, p.apparaatnaam)}>
                        Verwijderen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {passkeysBeschikbaar() ? (
          <div style={{ marginTop: 18, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input className="input" type="text" placeholder="Naam van dit apparaat (bijv. iPhone van Isaac)" value={apparaatnaam} onChange={(e) => setApparaatnaam(e.target.value)} style={{ flex: 1, minWidth: 220 }} />
            <button className="btn btn-primary" onClick={registreer} disabled={pkBezig}>
              {pkBezig ? <><span className="spinner" /> Bezig...</> : 'Registreer dit apparaat'}
            </button>
          </div>
        ) : (
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 14 }}>
            Passkeys worden niet ondersteund in deze browser.
          </p>
        )}
      </div>
    </div>
  );
}
